import { UiMode } from './uiMode';

export enum EventTrackerSectionsEnum {
  INTRAFARM = 'intrafarm',
  MULTIFARM = 'multifarm',
  CONNECT = 'connect', // (if user comes from Settings or from Menu, we use the same name)
  NEWS = 'news',
  ACTIVITIES = 'activities',
  FIELDS = 'fields',
  MAP = 'map',
  DASHBOARD = 'dashboard',
  WAREHOUSES = 'warehouses',
  PRODUCTS = 'products',
  LOADS = 'loads',
  SILOS = 'silos',
  COMMUNICATIONS = 'communications',
  WEATHER = 'weather',
  SENSORS = 'sensors',
  NUTRITION = 'nutrition',
  IRRIGATION = 'irrigation',
  AUTOMATIC_IRRIGATION = 'automatic_irrigation',
  PATHOGENS = 'pathogens',
  INSECTS = 'insects',
  DISEASE_RECOGNITION = 'disease_recognition',
  NOTES = 'notes',
  ALERTS = 'alerts', // (if user comes from Top Menu or from the Management section of the Menu, we use the same name)
  FINANCES = 'finances',
  OPERATIONAL_MANAGEMENT = 'operational_management',
  TASK_PLAN = 'task_plan',
  SUSTAINABILITY = 'sustainability',
  CHECKS = 'checks',
  MACHINERY = 'machinery',
  INCAB_CLOUD = 'incab_cloud',
  INCAB = 'incab',
  SATELLITE = 'satellite',
  PRESCRIPTION_MAPS = 'prescription_maps',
  ANALYSIS = 'analysis',
  TASK_VIEWER = 'task_viewer',
  INSURANCE = 'insurance',
  CONTRACTS = 'contracts',
  MY_FARM = 'my_farm',
  SCAN = 'scan',
  SETTINGS = 'settings',
  EXPORT_DOCUMENTS = 'export_documents',
  BARNS = 'barns',
  ANIMALS = 'animals',
  RATIONS = 'rations',
  SURVEY = 'survey',
  PRODUCTION_BATCHES = 'production_batches',
  CROP_PLANNING = 'crop_planning',
  MENU = 'menu',
  AUDIT = 'audit',
  SUPPORT = 'support',
  STORE = 'store',
  TUTORIAL = 'tutorial',
  WELCOME = 'welcome',
  SHARING = 'sharing',
  // FOLLOWING ARE NOT SECTIONS, BUT NEEDED FOR SPECIAL EVENTS
  LOGIN = 'login',
  OPEN_APPLICATION_WEB = 'open_application_web',
  CLOSE_APPLICATION_WEB = 'close_application_web',
  LOGOUT = 'logout',
}
export type EventTrackerAvailableSection = `${EventTrackerSectionsEnum}`;

type ModuleUrl = string;

//* Modules events. Key is the url after 'auth/' and value is the section name
// If the module is not listed here, it won't be tracked
// Note: some modules have subroutes, for example 'company-data/connections' or 'satellite/manage-prescription'
// In that case, we need to list the full path after 'auth/' to be able to track it correctly
const farmModulesUrlMap: Record<ModuleUrl, EventTrackerAvailableSection> = {
  activities: 'activities',
  alerts: 'alerts',
  satellite: 'satellite',
  checks: 'checks',
  training: 'communications',
  connect: 'connect',
  'company-data/connections': 'connect',
  'contract-management': 'contracts',
  'crop-planning': 'crop_planning',
  dashboard: 'dashboard',
  'import-export': 'export_documents',
  financials: 'finances',
  fields: 'fields',
  insects: 'insects',
  irrigation: 'irrigation',
  loads: 'loads',
  equipments: 'machinery',
  map: 'map',
  news: 'news',
  notes: 'notes',
  nutrition: 'nutrition',
  'operation-management': 'operational_management',
  diseases: 'pathogens',
  batchs: 'production_batches',
  products: 'products',
  sensors: 'sensors',
  silos: 'silos',
  sustainability: 'sustainability',
  'task-viewer': 'task_viewer',
  'task-plan': 'task_plan',
  survey: 'survey',
  containers: 'warehouses',
  weather: 'weather',
  'satellite/manage-files': 'analysis',
  'satellite/manage-prescription': 'prescription_maps',
};
const livestockModulesUrlMap: Record<ModuleUrl, EventTrackerAvailableSection> = {
  'stable-activity': 'activities',
  alerts: 'alerts',
  animals: 'animals',
  'stable-loads': 'loads',
  stables: 'barns',
  training: 'communications',
  connect: 'connect',
  'company-data/connections': 'connect',
  dashboard: 'dashboard',
  'import-export': 'export_documents',
  equipments: 'machinery',
  map: 'map',
  news: 'news',
  notes: 'notes',
  products: 'products',
  rations: 'rations',
  sensors: 'sensors',
  silos: 'silos',
  survey: 'survey',
  'task-viewer': 'task_viewer',
  containers: 'warehouses',
  weather: 'weather',
  'contract-management': 'contracts',
  'satellite/manage-files': 'analysis',
  'satellite/manage-prescription': 'prescription_maps',
};

export const EventTrackerModulesMapper: Record<
  UiMode,
  Record<ModuleUrl, EventTrackerAvailableSection>
> = {
  FARM: farmModulesUrlMap,
  LIVESTOCK: livestockModulesUrlMap,
};

// Click/static events
export enum EventTracker {
  CLICK_MENU_ITEM = 'click_menu_item',
  CLICK_LIVESTOCK_DASHBOARD_CREATE_ACTIVITY = 'click_livestock_dashboard_create_activity',
  CLICK_LIVESTOCK_DASHBOARD_MAP = 'click_livestock_dashboard_map',
  CLICK_AGRO_DASHBOARD_MAP = 'click_agro_dashboard_map',
  CLICK_AGRO_DASHBOARD_CREATE_WAREHOUSE_MOVEMENT = 'click_agro_dashboard_create_warehouse_movement',
  CLICK_AGRO_DASHBOARD_CREATE_ACTIVITY = 'click_agro_dashboard_create_activity',
  CLICK_TOP_BAR_LANGUAGE = 'click_top_bar_language',
  CLICK_TOP_BAR_NOTIFICATION_CENTER = 'click_top_bar_notification_center',
  CLICK_TOP_BAR_RELATED_COMPANIES = 'click_top_bar_related_companies',
  CLICK_LIVESTOCK_DASHBOARD_CRATE_SILOS_MOVEMENT = 'click_livestock_dashboard_create_silos_movement',
  CLICK_AGRO_PRESCRIPTION_MAPS = 'click_agro_prescription_maps',
  CLICK_AGRO_MENU_INCAB = 'click_agro_menu_incab',
  CLICK_LIVESTOCK_MENU_INCAB = 'click_livestock_menu_incab',
  CLICK_SETTINGS_CONNECT = 'click_settings_connect',
  LOGIN = 'login',
  OPEN_APPLICATION_WEB = 'open_application_web',
  CLOSE_APPLICATION_WEB = 'close_application_web',
  LOGOUT = 'logout',
  SELECT_INTRAFARM = 'select_intrafarm',
  SELECT_MULTIFARM = 'select_multifarm',
}

// Mapping click/static events to sections
export const EventTrackerClickMapper: Record<EventTracker, EventTrackerAvailableSection> = {
  [EventTracker.CLICK_MENU_ITEM]: 'menu',
  [EventTracker.CLICK_LIVESTOCK_DASHBOARD_CREATE_ACTIVITY]: 'activities',
  [EventTracker.CLICK_LIVESTOCK_DASHBOARD_MAP]: 'map',
  [EventTracker.CLICK_AGRO_DASHBOARD_MAP]: 'map',
  [EventTracker.CLICK_AGRO_DASHBOARD_CREATE_WAREHOUSE_MOVEMENT]: 'warehouses',
  [EventTracker.CLICK_AGRO_DASHBOARD_CREATE_ACTIVITY]: 'activities',
  [EventTracker.CLICK_TOP_BAR_LANGUAGE]: 'menu',
  [EventTracker.CLICK_TOP_BAR_NOTIFICATION_CENTER]: 'menu',
  [EventTracker.CLICK_TOP_BAR_RELATED_COMPANIES]: 'menu',
  [EventTracker.CLICK_LIVESTOCK_DASHBOARD_CRATE_SILOS_MOVEMENT]: 'silos',
  [EventTracker.CLICK_AGRO_PRESCRIPTION_MAPS]: 'prescription_maps',
  [EventTracker.CLICK_AGRO_MENU_INCAB]: 'incab',
  [EventTracker.CLICK_LIVESTOCK_MENU_INCAB]: 'incab',
  [EventTracker.CLICK_SETTINGS_CONNECT]: 'connect',
  [EventTracker.SELECT_INTRAFARM]: 'intrafarm',
  [EventTracker.SELECT_MULTIFARM]: 'multifarm',
  [EventTracker.LOGIN]: 'login',
  [EventTracker.OPEN_APPLICATION_WEB]: 'open_application_web',
  [EventTracker.CLOSE_APPLICATION_WEB]: 'close_application_web',
  [EventTracker.LOGOUT]: 'logout',
};
